import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";
import { useEffect, useState, type MutableRefObject } from "react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useTranslation } from "react-i18next";

import { OrderLineVideoLabelEditor } from "@/features/marketplace/components/OrderLineVideoLabelEditor";
import { ScriptReviewAction } from "@/features/marketplace/components/ScriptReviewAction";
import { OrderLineVideoStatusBadge } from "@/features/marketplace/components/OrderLineVideoStatusBadge";
import { orderLineVideoDisplayName } from "@/features/marketplace/lib/order-line-videos";
import { PartnerVideoSlotUpload } from "@/features/marketplace/partner/components/PartnerVideoSlotUpload";
import type { PartnerPreflightSuccessState } from "@/features/marketplace/partner/components/PartnerVideoUploadSlot";
import { isPartnerLegacyLineNotifyRetry } from "@/features/marketplace/partner/lib/partner-preflight-retry";
import {
  canPartnerAddVideoSlot,
  canPartnerDeleteEmptyVideoSlot,
  canPartnerManageVideoSlot,
  partnerLineVideos,
  partnerVideoSelectionKey,
  partnerVideoSlotEmpty,
} from "@/features/marketplace/partner/lib/partner-videos";
import type {
  OrderLineVideo,
  OrderScriptRow,
  PartnerOrder,
} from "@/features/marketplace/types/marketplace.types";
import { useUpdateOrderScriptVideoLabel } from "@/lib/queries/marketplace.queries";
import { useDeletePartnerOrderScriptVideoSlot } from "@/lib/queries/marketplace-partner.queries";

export function PartnerLineVideos({
  order,
  script,
  title,
  abortRef,
  onUploadSuccess,
  onAddVideoSlot,
  isAddingVideoSlot,
  initialExpandedSlotKey = null,
}: {
  order: PartnerOrder;
  script: OrderScriptRow;
  title: string;
  abortRef: MutableRefObject<AbortController | null>;
  onUploadSuccess: (state: PartnerPreflightSuccessState) => void;
  onAddVideoSlot: () => void;
  isAddingVideoSlot: boolean;
  initialExpandedSlotKey?: string | null;
}) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((state) => state.activeTenantId);
  const updateLabel = useUpdateOrderScriptVideoLabel();
  const removeEmptySlot = useDeletePartnerOrderScriptVideoSlot();
  const videos = partnerLineVideos(script);
  const [expandedSlotKey, setExpandedSlotKey] = useState<string | null>(null);
  const [busySlotKey, setBusySlotKey] = useState<string | null>(null);
  const [deleteSlotId, setDeleteSlotId] = useState<string | null>(null);

  useEffect(() => {
    if (initialExpandedSlotKey) {
      setExpandedSlotKey(initialExpandedSlotKey);
    }
  }, [initialExpandedSlotKey, script.id]);

  if (videos.length === 0) return null;

  const showAddVideoSlotButton =
    canPartnerAddVideoSlot(script) && !isPartnerLegacyLineNotifyRetry(script);

  return (
    <div className="flex flex-col gap-2">
      <ul
        aria-label={t("marketplace.partner.videos.listLabel", "Videos")}
        className="flex flex-col gap-2"
      >
        {videos.map((video) => {
          const label = orderLineVideoDisplayName(video, t);
          const isEmpty = partnerVideoSlotEmpty(video);
          const slotKey = partnerVideoSelectionKey(video);
          const canManage = canPartnerManageVideoSlot(script, video);
          const canRemoveEmpty =
            video.id !== null && canPartnerDeleteEmptyVideoSlot(script, video);
          const expanded = expandedSlotKey === slotKey;
          const slotLocked = busySlotKey !== null && busySlotKey !== slotKey;

          return (
            <li
              key={slotKey}
              className="flex flex-col gap-1 rounded-md bg-muted/40 px-3 py-2"
            >
              <div className="flex min-h-11 flex-wrap items-center justify-between gap-2">
                <OrderLineVideoLabelEditor
                  video={video}
                  editable={canManage && video.id !== null}
                  isPending={updateLabel.isPending}
                  onSave={(nextLabel) => {
                    if (!video.id) return;
                    updateLabel.mutate({
                      orderId: script.orderId,
                      orderScriptId: script.id,
                      deliverableId: video.id,
                      label: nextLabel,
                    });
                  }}
                />
                <OrderLineVideoStatusBadge
                  scriptFulfillmentStatus={script.fulfillmentStatus}
                  video={video}
                  videos={videos}
                  viewer="partner"
                />
              </div>
              {video.revisionNote?.trim() ? (
                <p className="text-[11px] leading-4 text-warning-subtle-foreground">
                  {video.revisionNote}
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                {video.scriptReviewId || video.preflightUrl ? (
                  <ScriptReviewAction
                    scriptReviewId={video.scriptReviewId}
                    preflightUrl={video.preflightUrl}
                    label={t("marketplace.partner.actions.view", "View")}
                    ariaLabel={t("marketplace.partner.videos.viewAria", {
                      label,
                      title,
                      defaultValue: "View review for {{label}} on {{title}}",
                    })}
                    variant="outline"
                    size="sm"
                    compact
                  />
                ) : null}
                {canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant={expanded ? "default" : "outline"}
                    aria-expanded={expanded}
                    onClick={() =>
                      setExpandedSlotKey(expanded ? null : slotKey)
                    }
                  >
                    {isEmpty
                      ? t("marketplace.partner.videos.addVideo", "Add video")
                      : t(
                          "marketplace.partner.videos.updateVideo",
                          "Update video",
                        )}
                  </Button>
                ) : null}
                {canRemoveEmpty ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={removeEmptySlot.isPending}
                    onClick={() => setDeleteSlotId(video.id)}
                  >
                    {t(
                      "marketplace.partner.videos.removeEmptySlot",
                      "Remove slot",
                    )}
                  </Button>
                ) : null}
              </div>
              {expanded && canManage ? (
                <PartnerVideoSlotUpload
                  order={order}
                  script={script}
                  video={video}
                  title={title}
                  abortRef={abortRef}
                  isLocked={slotLocked}
                  onBusyChange={(busy) => {
                    setBusySlotKey(busy ? slotKey : null);
                  }}
                  onUploadSuccess={(state) => {
                    setExpandedSlotKey(null);
                    setBusySlotKey(null);
                    onUploadSuccess(state);
                  }}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
      {showAddVideoSlotButton ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start border-primary/40 font-medium"
          disabled={isAddingVideoSlot}
          onClick={onAddVideoSlot}
        >
          {t(
            "marketplace.partner.videos.addAnotherToScript",
            "Add another video to this script",
          )}
        </Button>
      ) : null}
      <Dialog
        open={deleteSlotId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteSlotId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(
                "marketplace.partner.videos.removeEmptySlotTitle",
                "Remove this empty video slot?",
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "marketplace.partner.videos.removeEmptySlotDescription",
                "The slot is removed from this script. Uploaded videos on other slots are not affected.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteSlotId(null)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={removeEmptySlot.isPending || !deleteSlotId || !activeTenantId}
              onClick={() => {
                if (!deleteSlotId || !activeTenantId) return;
                removeEmptySlot.mutate(
                  {
                    partnerTenantId: activeTenantId,
                    orderId: order.id,
                    orderScriptId: script.id,
                    deliverableId: deleteSlotId,
                  },
                  {
                    onSuccess: () => {
                      setDeleteSlotId(null);
                      if (expandedSlotKey) {
                        const removed = videos.find((v) => v.id === deleteSlotId);
                        if (
                          removed &&
                          partnerVideoSelectionKey(removed) === expandedSlotKey
                        ) {
                          setExpandedSlotKey(null);
                        }
                      }
                    },
                  },
                );
              }}
            >
              {t("marketplace.partner.videos.removeEmptySlotConfirm", "Remove")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
